import { memo, useCallback, useMemo } from 'react';
import { MathUtils } from 'three';
import { ConstellationSpec as ConstellationSpecType } from '@thive/fddm-types';

import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { SelectInput } from '@/modules/common/components/inputs';
import { useReadSelectedAreas } from '@/modules/floorplanDesign/hooks';
import {
  areasToFlatCollection,
  isSingularStationArea,
  isStationaryArea,
} from '@/modules/floorplanDesign/helpers';
import { Spacing } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/common/Spacing';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { NumUtils } from '@/modules/common/utils';
import { useCanvasStore } from '@/modules/canvas/store';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { poiMapper, stationMapper } from '@/modules/canvas/mappers';
import { getDuplicateStationIds } from '@/modules/canvas/mappers/stationMapper';
import { rotatePoint } from '@/modules/common/helpers/math';
import { Angle } from './Angle';
import { ANGLE_EPSILON_LOOSE as ANGLE_EPSILON } from '@/helpers/angleConstants';

const DISTRIBUTION_OPTIONS: {
  label: string;
  value: ConstellationSpecType['distribution'];
}[] = [
  { label: 'Margin', value: 'MARGIN' },
  { label: 'Gap', value: 'GAP' },
];

const STACKING_OPTIONS: {
  label: string;
  value: ConstellationSpecType['stacking'];
}[] = [
  { label: 'Horizontal top', value: 'HORIZONTAL_TOP' },
  { label: 'Horizontal middle', value: 'HORIZONTAL_MIDDLE' },
  { label: 'Horizontal bottom', value: 'HORIZONTAL_BOTTOM' },
  { label: 'Vertical left', value: 'VERTICAL_LEFT' },
  { label: 'Vertical middle', value: 'VERTICAL_MIDDLE' },
  { label: 'Vertical right', value: 'VERTICAL_RIGHT' },
];

function ConstellationSpecComponent() {
  const selectedAreas = useReadSelectedAreas();

  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const area = useMemo(() => {
    const areas = areasToFlatCollection(selectedAreas);
    if (areas.length !== 1) return undefined;

    const candidate = areas[0];
    if (isSingularStationArea(candidate) || isStationaryArea(candidate)) return candidate;

    return undefined;
  }, [selectedAreas]);

  const preferenceKey = useMemo(() => {
    if (!area) return undefined;
    if (isSingularStationArea(area)) return 'singularStationArea.constellationSpec';
    if (isStationaryArea(area)) return 'stationaryArea.constellationSpec';

    return undefined;
  }, [area]);

  const loadHandlingAngle = useMemo(() => {
    if (area && isSingularStationArea(area)) {
      return area.loadHandlingPoiSpecs[0]?.angle ?? 0;
    }

    return 0;
  }, [area]);

  const constellationSpec = area?.constellationSpec;

  const spacingIconRotationDeg = useMemo(() => {
    if (!area || !constellationSpec) return 0;

    return (
      90 + MathUtils.radToDeg(-area.rectangle.angle + -constellationSpec.angle + loadHandlingAngle)
    );
  }, [area, constellationSpec, loadHandlingAngle]);

  /**
   * Rotates POI and station canvas elements around the area center while dragging.
   * Preview only; backend regenerates final positions on commit.
   */
  const onAnglePreview = useCallback(
    (newAngleRad: number) => {
      if (!area) return;

      const canvas = useCanvasStore.getState().instance;
      if (!canvas) return;

      const { floorplan } = useFloorplanDesignStore.getState();
      if (!floorplan) return;

      const deltaRad = newAngleRad - area.constellationSpec.angle;
      if (Math.abs(deltaRad) < ANGLE_EPSILON) return;

      const areaStations = floorplan.stations.filter((station) =>
        station.areaIds.includes(area.id),
      );
      const stationsById = new Map(areaStations.map((station) => [station.id, station]));

      // Constellation direction changes facing angle and rotates the configured vehicle offset.
      // Station centers stay fixed; POI pose and POI rectangle centers rotate around their linked station.
      const rotatedPois = floorplan.vehicleLayouts
        .flatMap((vehicleLayout) => vehicleLayout.pois)
        .filter((poi) => poi.areaId === area.id)
        .map((poi) => {
          const station = poi.stationIds
            .map((stationId) => stationsById.get(stationId))
            .find((candidate) => Boolean(candidate));

          if (!station) {
            const rectangleCenter = rotatePoint(
              poi.rectangle.centerX,
              poi.rectangle.centerY,
              poi.pose.x,
              poi.pose.y,
              deltaRad,
            );

            return {
              ...poi,
              rectangle: {
                ...poi.rectangle,
                centerX: rectangleCenter.x,
                centerY: rectangleCenter.y,
                angle: poi.rectangle.angle + deltaRad,
              },
              pose: {
                ...poi.pose,
                angle: poi.pose.angle + deltaRad,
              },
            };
          }

          const stationRectangle = station.cuboid.rectangle;
          const pose = rotatePoint(
            poi.pose.x,
            poi.pose.y,
            stationRectangle.centerX,
            stationRectangle.centerY,
            deltaRad,
          );
          const rectangleCenter = rotatePoint(
            poi.rectangle.centerX,
            poi.rectangle.centerY,
            stationRectangle.centerX,
            stationRectangle.centerY,
            deltaRad,
          );

          return {
            ...poi,
            rectangle: {
              ...poi.rectangle,
              centerX: rectangleCenter.x,
              centerY: rectangleCenter.y,
              angle: poi.rectangle.angle + deltaRad,
            },
            pose: {
              ...poi.pose,
              x: pose.x,
              y: pose.y,
              angle: poi.pose.angle + deltaRad,
            },
          };
        });

      const rotatedStations = areaStations.map((station) => ({
        ...station,
        cuboid: {
          ...station.cuboid,
          rectangle: {
            ...station.cuboid.rectangle,
            angle: station.cuboid.rectangle.angle + deltaRad,
          },
        },
      }));

      const zoom = canvas.getZoom();
      const duplicateStationIds = getDuplicateStationIds(rotatedStations);

      if (duplicateStationIds.length > 0) {
        canvas.removeElements(duplicateStationIds);
      }

      canvas.updateElements([
        ...poiMapper(rotatedPois, [], zoom, true, true),
        ...stationMapper(rotatedStations, true),
      ]);
    },
    [area],
  );

  const onAngleCommit = useCallback(
    (angleRad: number) => {
      if (!area || !preferenceKey) return;

      fddmSvc.setPreference(`${preferenceKey}.angle`, angleRad);
      fddmSvc.updateArea({
        ...area,
        constellationSpec: {
          ...area.constellationSpec,
          angle: angleRad,
        },
      });
    },
    [area, fddmSvc, preferenceKey],
  );

  const onStackingChange = useCallback(
    (newValue: ConstellationSpecType['stacking']) => {
      if (!area || !preferenceKey) return;

      fddmSvc.setPreference(`${preferenceKey}.stacking`, newValue);
      fddmSvc.updateArea({
        ...area,
        constellationSpec: {
          ...area.constellationSpec,
          stacking: newValue,
        },
      });
    },
    [area, fddmSvc, preferenceKey],
  );

  const onMinMarginChange = useCallback(
    (newValueMm: number) => {
      if (!area || !preferenceKey || Number.isNaN(newValueMm)) return;

      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference(`${preferenceKey}.spacing.minMargin`, newValue);
      fddmSvc.updateArea({
        ...area,
        constellationSpec: {
          ...area.constellationSpec,
          spacing: {
            ...area.constellationSpec.spacing,
            minMargin: newValue,
          },
        },
      });
    },
    [area, fddmSvc, preferenceKey],
  );

  const onMinGapChange = useCallback(
    (newValueMm: number) => {
      if (!area || !preferenceKey || Number.isNaN(newValueMm)) return;

      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference(`${preferenceKey}.spacing.minGap`, newValue);
      fddmSvc.updateArea({
        ...area,
        constellationSpec: {
          ...area.constellationSpec,
          spacing: {
            ...area.constellationSpec.spacing,
            minGap: newValue,
          },
        },
      });
    },
    [area, fddmSvc, preferenceKey],
  );

  const onDistributionChange = useCallback(
    (newValue: ConstellationSpecType['distribution']) => {
      if (!area || !preferenceKey) return;

      fddmSvc.setPreference(`${preferenceKey}.distribution`, newValue);
      fddmSvc.updateArea({
        ...area,
        constellationSpec: {
          ...area.constellationSpec,
          distribution: newValue,
        },
      });
    },
    [area, fddmSvc, preferenceKey],
  );

  if (!area || !constellationSpec) return null;

  return (
    <>
      <Angle
        parentRotationRad={area.rectangle.angle + loadHandlingAngle}
        valueRad={constellationSpec.angle}
        onCommit={onAngleCommit}
        onPreview={onAnglePreview}
      />

      <PropertiesLabel i18nkey='interface:properties.area.stacking'>
        <SelectInput
          options={STACKING_OPTIONS}
          onChange={onStackingChange}
          value={constellationSpec.stacking}
        />
      </PropertiesLabel>

      <PropertiesLabel i18nkey='interface:properties.area.spacing'>
        <Spacing
          margin={NumUtils.metersToMmRounded(constellationSpec.spacing.minMargin)}
          gap={NumUtils.metersToMmRounded(constellationSpec.spacing.minGap)}
          iconRotationDeg={spacingIconRotationDeg}
          onMarginChange={onMinMarginChange}
          onGapChange={onMinGapChange}
        />
      </PropertiesLabel>

      <PropertiesLabel i18nkey='interface:properties.area.distribution'>
        <SelectInput
          options={DISTRIBUTION_OPTIONS}
          onChange={onDistributionChange}
          value={constellationSpec.distribution}
        />
      </PropertiesLabel>
    </>
  );
}

export const ConstellationSpec = memo(ConstellationSpecComponent);
